import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  FitnessCenter,
  Search,
  Refresh,
  OpenInNew,
  WhatsApp,
  Email,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';

interface StudioEnquiry {
  _id: string;
  studioName: string;
  city: string;
  instagramOrWebsite: string;
  offering: string;
  contactName: string;
  role: string;
  email: string;
  whatsapp: string;
  interestedIn: string[];
  interestedInOther: string;
  hearAboutUs: string;
  hearAboutUsOther: string;
  status: string;
  adminNotes: string;
  createdAt: string;
}

const STATUS_OPTIONS = ['new', 'contacted', 'converted', 'not_interested'];

const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' => {
  switch (s) {
    case 'new': return 'info';
    case 'contacted': return 'warning';
    case 'converted': return 'success';
    case 'not_interested': return 'error';
    default: return 'default';
  }
};

const statusLabel = (s: string) =>
  s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

const StudioEnquiriesPage: React.FC = () => {
  const [enquiries, setEnquiries] = useState<StudioEnquiry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selected, setSelected] = useState<StudioEnquiry | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(rowsPerPage),
      });
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);

      const res: any = await apiService.get(
        `${API_ENDPOINTS.STUDIO_ENQUIRY.LIST}?${params.toString()}`,
      );
      setEnquiries(res.enquiries || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, filterStatus]);

  useEffect(() => { fetchEnquiries(); }, [fetchEnquiries]);

  const openDetail = (row: StudioEnquiry) => {
    setSelected(row);
    setEditStatus(row.status);
    setEditNotes(row.adminNotes || '');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSavingStatus(true);
    try {
      await apiService.patch(API_ENDPOINTS.STUDIO_ENQUIRY.UPDATE(selected._id), {
        status: editStatus,
        adminNotes: editNotes,
      });
      setSelected(null);
      fetchEnquiries();
    } finally {
      setSavingStatus(false);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <FitnessCenter sx={{ color: 'text.secondary', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Studio Enquiries</Typography>
          <Typography variant="body2" color="text.secondary">
            Leads from the "Get Started" form on the website
          </Typography>
        </Box>
        <Chip label={`${total} total`} variant="outlined" size="small" />
        <IconButton onClick={fetchEnquiries} title="Refresh"><Refresh /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search studio, name, email, phone…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              sx={{ width: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card variant="outlined">
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Studio</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Offering</TableCell>
                    <TableCell>Interested In</TableCell>
                    <TableCell>Heard Via</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enquiries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                        <Typography color="text.secondary">No enquiries found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    enquiries.map(row => (
                      <TableRow key={row._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{row.studioName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.instagramOrWebsite}</Typography>
                        </TableCell>
                        <TableCell>{row.city}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.contactName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.role}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>{row.offering}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {row.interestedIn?.map(i => (
                              <Chip key={i} label={i === 'Other' && row.interestedInOther ? row.interestedInOther : i} size="small" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {row.hearAboutUs === 'Other' && row.hearAboutUsOther
                            ? row.hearAboutUsOther
                            : row.hearAboutUs}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={statusLabel(row.status)} color={statusColor(row.status)} size="small" />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {formatDate(row.createdAt)}
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => openDetail(row)}>
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[20]}
            />
          </>
        )}
      </Card>

      {/* Detail / Edit dialog */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Typography fontWeight={700}>{selected.studioName}</Typography>
              <Typography variant="body2" color="text.secondary">{selected.city} · {selected.offering}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {/* Contact info */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Contact</Typography>
                  <Typography>{selected.contactName} — {selected.role}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip icon={<Email fontSize="small" />} label={selected.email} size="small" variant="outlined" />
                    <Chip icon={<WhatsApp fontSize="small" />} label={`+91 ${selected.whatsapp}`} size="small" variant="outlined" color="success" />
                  </Box>
                </Box>

                <Divider />

                {/* Instagram / Website */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Instagram / Website</Typography>
                  <Typography variant="body2">{selected.instagramOrWebsite}</Typography>
                </Box>

                {/* Interested in */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Interested In</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selected.interestedIn?.map(i => (
                      <Chip key={i} label={i} size="small" color="primary" variant="outlined" />
                    ))}
                    {selected.interestedInOther && (
                      <Chip label={`Other: ${selected.interestedInOther}`} size="small" />
                    )}
                  </Box>
                </Box>

                {/* Heard about us */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>How did they hear about us?</Typography>
                  <Typography variant="body2">
                    {selected.hearAboutUs}
                    {selected.hearAboutUsOther && ` — ${selected.hearAboutUsOther}`}
                  </Typography>
                </Box>

                <Divider />

                {/* Admin controls */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={editStatus} label="Status" onChange={e => setEditStatus(e.target.value)}>
                    {STATUS_OPTIONS.map(s => (
                      <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Admin Notes"
                  multiline
                  minRows={3}
                  fullWidth
                  size="small"
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  placeholder="Add internal notes…"
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={savingStatus}>
                {savingStatus ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default StudioEnquiriesPage;
